import React from 'react';
import { Pencil } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ADMIN_EMAIL = 'mmaary.2007@gmail.com';

/**
 * EditableImage — renders a normal <img> for customers, but for the
 * signed-in admin overlays a small pencil badge that jumps to the exact
 * slot in the Site Images admin panel.
 *
 * Props:
 *   - siteKey: the registered image key (e.g. "home.hero.main")
 *   - src, alt, className, ...rest: forwarded to <img>
 *   - overlayPosition: "tr" | "tl" | "br" | "bl" (default "tr")
 */
const EditableImage = ({
  siteKey,
  src,
  alt = '',
  className = '',
  overlayPosition = 'tr',
  imgClassName,
  ...rest
}) => {
  const { user, isAuthenticated } = useAuth();
  const isAdmin = isAuthenticated && user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();

  // Fallback placeholder — prevents "empty string src" browser warnings and
  // gives production (empty site_images) something graceful to render.
  const safeSrc = src && src.trim() !== '' ? src : '/lotus.png';

  // A raw <img> when we don't need the wrapper overhead
  if (!isAdmin || !siteKey) {
    return <img src={safeSrc} alt={alt} className={imgClassName || className} {...rest} />;
  }

  const pos = {
    tr: 'top-3 right-3',
    tl: 'top-3 left-3',
    br: 'bottom-3 right-3',
    bl: 'bottom-3 left-3',
  }[overlayPosition] || 'top-3 right-3';

  return (
    <span className="relative inline-block w-full h-full group/edit">
      <img src={safeSrc} alt={alt} className={imgClassName || className} {...rest} />
      <Link
        to={`/admin?tab=site-images&focus=${encodeURIComponent(siteKey)}`}
        onClick={(e) => e.stopPropagation()}
        aria-label="Edit this image in admin"
        title={`Edit: ${siteKey}`}
        data-testid={`edit-image-${siteKey}`}
        className={`absolute ${pos} z-30 opacity-0 group-hover/edit:opacity-100 hover:opacity-100 focus:opacity-100 transition-opacity duration-200 bg-white/95 backdrop-blur-sm text-brand-espresso text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 rounded-full shadow-luxe flex items-center gap-1.5 border border-brand-nude no-underline`}
      >
        <Pencil size={11} strokeWidth={1.8} />
        Edit
      </Link>
    </span>
  );
};

export default EditableImage;
